/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {forwardRef, useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'
import {useLocation} from 'react-router-dom'
import {
    Flex,
    Button,
    Skeleton,
    Box,
    Text,
    VStack,
    Fade,
    Stack,
    Select,
    Link as LinkPlaceholder,
    Image,
    useStyleConfig,
    IconButton
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useDerivedProduct} from '../../hooks'

// project components
import ImageGalleryCompact from '../image-gallery-compact'
import {Skeleton as ImageGallerySkeleton} from '@salesforce/retail-react-app/app/components/image-gallery'
import DisplayPrice from '../display-price'
import Swatch from '../swatch-group/swatch'
import SwatchGroup from '../swatch-group'
import {getItemBadge, getPriceData} from '../../utils/product-utils'
import {getUrl} from '../../utils/cloudinary-utils'
import {motion} from 'framer-motion'
import withRegistration from '../with-registration'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import QuantityPicker from '../quantity-picker'
import LoadingSpinner from '../loading-spinner'
const MotionBox = motion(Box)
const IconButtonWithRegistration = withRegistration(IconButton)
import {formattedURL} from '../../utils/url'
import {useGlobaleContext} from '../../contexts/globale'
import {useSiteContext} from '../../contexts/site'
import Description from './partials/description'
import {useAddToCartDrawerContext} from '../../hooks/use-add-to-cart-drawer'
import useMonetate from '../../hooks/use-monetate'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import useTagManager from '../../hooks/use-tag-manager'
import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react'
const LARGE = 'large'

const ProductViewHeader = ({
    name,
    currency,
    priceData,
    globalePrices,
    promoId,
    product,
    isProductPartOfBundle,
    showDescription,
    onTileClick
}) => {
    const styles = useStyleConfig('ProductViewCompactStyles')
    const {countryISO} = useGlobaleContext()
    const productUrl = product?.slugUrl
    const productUrlFormatted = formattedURL(productUrl)
    if (product && globalePrices) {
        const promoObj = globalePrices?.find((p) => p?.[promoId])?.[promoId]
        priceData.currentPrice =
            promoObj?.[product?.id] !== null ? promoObj?.[product?.id] : product?.price || 0
        priceData.isOnSale = priceData.listPrice !== priceData.currentPrice
    }
    return (
        <VStack className="product-view-stack" {...styles.viewStack.body}>
            {/* Title */}
            <Skeleton isLoaded={name}>
                <Box
                    {...productUrlFormatted}
                    onClick={onTileClick}
                    className="product-name"
                    _hover={{
                        color: 'blue.700',
                        textDecoration: 'unset'
                    }}
                    {...styles.viewStack.productName}
                    pointerEvents={promoId ? 'none' : 'auto'}
                >
                    {name}
                </Box>
            </Skeleton>

            {!isProductPartOfBundle && (
                <>
                    <Skeleton isLoaded={priceData !== undefined && priceData !== null}>
                        {priceData && countryISO !== null && (
                            <DisplayPrice
                                priceData={priceData}
                                currency={product?.c_selectedCurrency || currency}
                                symbol={product?.c_selectedCurrencySymbol}
                            />
                        )}
                    </Skeleton>
                </>
            )}

            {showDescription && product?.shortDescription && (
                <Description description={product.shortDescription} />
            )}
        </VStack>
    )
}

ProductViewHeader.propTypes = {
    name: PropTypes.string,
    currency: PropTypes.string,
    globalePrices: PropTypes.any,
    promoId: PropTypes.any,
    category: PropTypes.array,
    priceData: PropTypes.object,
    product: PropTypes.object,
    isProductPartOfBundle: PropTypes.bool,
    showDescription: PropTypes.bool,
    onTileClick: PropTypes.func
}

/**
 * Render a product detail view that includes name, image gallery, price,
 * variant selections, action buttons
 */

const ProductViewCompact = forwardRef(
    (
        {
            product: productOriginal,
            category,
            imageSize = 'md',
            addToCart,
            globalePrices = null,
            promoId = null,
            limitReached = false,
            updateCart,
            meterType = null,
            isProductLoading,
            isProductPartOfSet = false,
            index,
            wishlist = {},
            onFavouriteToggle,
            enableFavourite = false,
            wishlistAnimation = false,
            isShopTheLook = false,
            isProductPartOfBundle = false,
            childOfBundleQuantity = 0,
            childProductOrderability,
            onVariantSelected = () => {},
            validateOrderability = (variant, quantity, stockLevel) =>
                !isProductLoading && variant?.orderable && quantity > 0 && quantity <= stockLevel,
            showImageGallery = true,
            selectedBundleParentQuantity = 1,
            showQtySelector = false,
            enableQtySelector = true,
            showSwatches = true,
            showAddToBag = true,
            maxOrderQuantity = 1,
            onTileClick = () => {},
            styleVariant = 'default',
            showDescription = false,
            showAddedToBagLabel = false
        },
        ref
    ) => {
        const {isYR, setOpenBonusModal} = useSiteContext()
        const {countryISO, currencyCode, globaleCurrency} = useGlobaleContext()
        const {currency: activeCurrency} = useCurrency()
        const intl = useIntl()
        const location = useLocation()
        const tagManager = useTagManager()
        const monetate = useMonetate()
        const api = useCommerceApi()
        const {getTokenWhenReady} = useAccessToken()
        const {onOpen: onAddToCartDrawerOpen, onClose: onAddToCartDrawerClose} =
            useAddToCartDrawerContext()
        const styles = useStyleConfig('ProductViewCompactStyles', {variant: styleVariant})
        const wishlistStyles = useStyleConfig('WishlistStyles')
        const formStyles = useStyleConfig('FormStyles')
        const [showOptionsMessage, toggleShowOptionsMessage] = useState(false)
        const [showAddToCartSpinner, setShowAddToCartSpinner] = useState(false)
        const [showChangeSwatchSpinner, setChangeSwatchSpinner] = useState(false)
        const [showAddedCta, setShowAddedCta] = useState(false)
        const [showRemovedCta, setShowRemovedCta] = useState(false)
        const [product, setProduct] = useState(productOriginal)
        const {
            showLoading,
            showInventoryMessage,
            inventoryMessage,
            quantity,
            setQuantity,
            variant,
            variationParams,
            variationAttributes,
            stockLevel
        } = useDerivedProduct(product, isProductPartOfSet, isProductPartOfBundle, false)
        const [productWithFilteredVariants, setProductWithFilteredVariants] = useState(product)
        let hasMetal = false
        const priceData = useMemo(() => {
            return getPriceData(productWithFilteredVariants)
        }, [product, quantity, productWithFilteredVariants])
        const isFavouriteLoading = useRef(false)
        const {data: customer} = useCurrentCustomer()
        const isProductASet = product?.type?.set
        const isProductABundle = product?.type?.bundle
        const errorContainerRef = useRef(null)
        const {disableButton, customInventoryMessage} = useMemo(() => {
            let shouldDisableButton = showInventoryMessage
            let currentInventoryMsg = ''
            if (
                !shouldDisableButton &&
                (isProductASet || isProductABundle) &&
                childProductOrderability
            ) {
                // if any of the children are not orderable, it will disable the Add to Bag button
                const unavailableChildProductKey = Object.keys(childProductOrderability).find(
                    (key) => {
                        return childProductOrderability[key].showInventoryMessage
                    }
                )
                shouldDisableButton = !!unavailableChildProductKey
                if (unavailableChildProductKey) {
                    const unavailableChildProduct =
                        childProductOrderability[unavailableChildProductKey]
                    if (unavailableChildProduct.unfulfillable) {
                        currentInventoryMsg = intl.formatMessage(
                            {
                                defaultMessage: 'Only {stockLevel} left for {productName}!',
                                id: 'use_product.message.inventory_remaining_for_product'
                            },
                            {
                                stockLevel: unavailableChildProduct.stockLevel,
                                productName: unavailableChildProduct.productName
                            }
                        )
                    }
                    if (unavailableChildProduct.isOutOfStock) {
                        currentInventoryMsg = intl.formatMessage(
                            {
                                defaultMessage: 'Out of stock for {productName}',
                                id: 'use_product.message.out_of_stock_for_product'
                            },
                            {productName: unavailableChildProduct.productName}
                        )
                    }
                }
            }
            return {disableButton: shouldDisableButton, customInventoryMessage: currentInventoryMsg}
        }, [showInventoryMessage, childProductOrderability])

        const validateAndShowError = () => {
            // Validate that all attributes are selected before proceeding.
            const hasValidSelection = validateOrderability(variant, quantity, stockLevel)
            const showError = !isProductASet && !isProductABundle && !hasValidSelection

            toggleShowOptionsMessage(showError)
            return hasValidSelection
        }

        const renderActionButtons = () => {
            const buttons = []
            const buttonText = {
                disabled: intl.formatMessage({
                    defaultMessage: 'Sold Out',
                    id: 'product_view.button.sold_out'
                }),
                update: intl.formatMessage({
                    defaultMessage: 'Update',
                    id: 'product_view.button.update'
                }),
                addToCart: intl.formatMessage({
                    defaultMessage: 'Add to Bag',
                    id: 'product_view.button.add_to_cart'
                }),
                addSetToCart: intl.formatMessage({
                    defaultMessage: 'Add Set to Cart',
                    id: 'product_view.button.add_set_to_cart'
                }),
                addBundleToCart: intl.formatMessage({
                    defaultMessage: 'Add Bundle to Cart',
                    id: 'product_view.button.add_bundle_to_cart'
                }),
                addedToCart: intl.formatMessage({
                    defaultMessage: 'Added',
                    id: 'product_view.button.added_to_cart'
                }),
                maxLimit: intl.formatMessage({
                    defaultMessage: 'Max Limit',
                    id: 'product_view.button.max_limit'
                }),
                removedFromCart: intl.formatMessage({
                    defaultMessage: 'Removed',
                    id: 'product_view.button.removed_from_cart'
                }),
                removeFromCart: intl.formatMessage({
                    defaultMessage: 'Remove',
                    id: 'product_view.button.remove_from_cart'
                }),
                addToWishlist: intl.formatMessage({
                    defaultMessage: 'Add to Wishlist',
                    id: 'product_view.button.add_to_wishlist'
                }),
                addSetToWishlist: intl.formatMessage({
                    defaultMessage: 'Add Set to Wishlist',
                    id: 'product_view.button.add_set_to_wishlist'
                }),
                addBundleToWishlist: intl.formatMessage({
                    defaultMessage: 'Add Bundle to Wishlist',
                    id: 'product_view.button.add_bundle_to_wishlist'
                })
            }

            const handleCartItem = async () => {
                const hasValidSelection = validateAndShowError()

                if (!hasValidSelection) {
                    return null
                }

                if (!addToCart && !updateCart) return null

                const method = isShopTheLook ? 'Shop The Look' : undefined
                const analyticsAttributes = JSON.stringify({
                    listID: method,
                    listName: method,
                    itemBadge: getItemBadge(product, {selectedVariant: variant})
                })
                if (!variant) {
                    product.c_analyticsAttributes = analyticsAttributes
                } else {
                    variant.c_analyticsAttributes = analyticsAttributes
                }

                if (updateCart) {
                    await updateCart(variant || product, quantity)
                    return
                }
                try {
                    setShowAddToCartSpinner(true)
                    const response = await addToCart(variant, quantity)
                    const {
                        productSelectionValues: itemsAdded,
                        basketResponse,
                        wasRemoved
                    } = response || {}
                    setShowAddToCartSpinner(false)
                    // Open modal only when `addToCart` returns some data
                    // It's possible that the item has been added to cart, but we don't want to open the modal.
                    // See wishlist_primary_action for example.
                    if (itemsAdded?.length) {
                        if (wasRemoved) {
                            setShowRemovedCta(true)
                            setShowAddedCta(false)
                        } else {
                            const heroImageGroup = findImageGroupBy(product?.imageGroups, {
                                viewType: LARGE,
                                selectedVariationAttributes: variationParams
                            })
                            if (styleVariant !== 'gwp') {
                                onAddToCartDrawerOpen({
                                    product,
                                    itemsAdded,
                                    selectedQuantity: quantity,
                                    priceData: priceData,
                                    heroImage: heroImageGroup?.images?.[0]?.disBaseLink
                                })
                                setOpenBonusModal(true)
                                monetate.monetateTrackPage(
                                    'addToBagModal',
                                    window.mtBreadcrumb,
                                    basketResponse?.productItems,
                                    customer?.customerType === 'registered',
                                    wishlist?.customerProductListItems?.length || 0,
                                    null,
                                    null,
                                    itemsAdded,
                                    basketResponse?.giftCertificateItems
                                )
                            }
                            setShowAddedCta(true)
                            const indexPosition = isShopTheLook ? index + 1 : undefined

                            const addToCartData = {
                                itemsAdded: itemsAdded,
                                method: method,
                                currency: globaleCurrency,
                                sorting: undefined,
                                indexPosition: indexPosition,
                                listName: method,
                                listId: method
                            }
                            tagManager.addToCartGA4(addToCartData)
                        }

                        window.setTimeout(() => {
                            setShowAddedCta(false)
                        }, 3000)

                        if (styleVariant === 'gwp') {
                            window.setTimeout(() => {
                                setShowRemovedCta(false)
                            }, 3000)
                        }
                    }
                } catch (e) {
                    setShowAddToCartSpinner(false)
                }
            }

            const buttonProps =
                styleVariant === 'gwp'
                    ? styles.viewWrapper.gwpButton(isYR, showAddedCta || showRemovedCta)
                    : isProductPartOfSet
                    ? styles.viewWrapper.setButton
                    : styles.viewWrapper.pdpButton
            // child product of bundles do not have Add to Bag button
            if ((addToCart || updateCart) && !isProductPartOfBundle) {
                const disabledBtnProps = disableButton
                    ? {
                          _hover: {
                              backgroundColor: 'gray.40',
                              color: 'gray.650'
                          },
                          backgroundColor: 'gray.40',
                          color: 'gray.650',
                          border: '0px solid'
                      }
                    : {}
                const isMaxLimitBtn =
                    limitReached &&
                    !(
                        disableButton ||
                        showAddedCta ||
                        (showAddedToBagLabel && styleVariant === 'gwp') ||
                        showRemovedCta
                    )
                buttons.push(
                    <Button
                        key="cart-button"
                        onClick={() => {
                            if (meterType && disableButton) {
                                tagManager.bonusSoldOut(product, category, meterType)
                                return
                            }
                            handleCartItem()
                        }}
                        isDisabled={
                            showRemovedCta ||
                            showAddedCta ||
                            isMaxLimitBtn ||
                            (disableButton && !meterType)
                        }
                        isLoading={showAddToCartSpinner}
                        flex="1"
                        fontSize="14px"
                        lineHeight="22px"
                        letterSpacing="0.07px"
                        {...buttonProps}
                        {...disabledBtnProps}
                    >
                        {isMaxLimitBtn
                            ? buttonText.maxLimit
                            : disableButton
                            ? buttonText.disabled
                            : showAddedCta
                            ? buttonText.addedToCart
                            : showAddedToBagLabel && styleVariant === 'gwp'
                            ? buttonText.removeFromCart
                            : updateCart
                            ? buttonText.update
                            : isProductASet
                            ? buttonText.addSetToCart
                            : isProductABundle
                            ? buttonText.addBundleToCart
                            : showRemovedCta
                            ? buttonText.removedFromCart
                            : buttonText.addToCart}
                    </Button>
                )
            }
            return buttons
        }

        // Bind the reference with our `scope` that includes the internal validate function for this component.
        // Other values can be added to this scope as required.
        if (typeof ref === 'function') {
            ref = ref.bind({validateOrderability: validateAndShowError})
        }

        // Set the quantity of bundle child in a product bundle to ensure availability messages appear
        if (
            isProductPartOfBundle &&
            quantity != selectedBundleParentQuantity * childOfBundleQuantity
        ) {
            setQuantity(selectedBundleParentQuantity * childOfBundleQuantity)
        }

        useEffect(() => {
            onAddToCartDrawerClose()
        }, [location?.pathname])

        useEffect(() => {
            if (
                !isProductASet &&
                !isProductABundle &&
                validateOrderability(variant, quantity, stockLevel)
            ) {
                toggleShowOptionsMessage(false)
            }
        }, [variationParams])

        useEffect(() => {
            if (variant) {
                onVariantSelected(product, variant, quantity)
            }
        }, [variant?.productId, quantity])

        const [hideMoreSwatches, setHideMoreSwatches] = useState(isShopTheLook)
        const productVariationAttributes =
            isProductPartOfSet || !product?.c_PDPSwatches
                ? variationAttributes
                : Object.entries(JSON.parse(product?.c_PDPSwatches))?.map(([category, ids]) => {
                      const originalSelected = variationAttributes[0].selectedValue

                      return {
                          id: category.toLowerCase().replace(/\s+/g, '-'),
                          name: category,
                          values: variationAttributes[0].values.filter(
                              (v) =>
                                  ids.length
                                      ? ids.includes(v.value) // Keep only values belonging to this category
                                      : !Object.values(JSON.parse(product?.c_PDPSwatches))
                                            .flat()
                                            .includes(v.value) // Exclude those already categorized
                          ),
                          selectedValue:
                              category === 'Original'
                                  ? originalSelected
                                  : originalSelected && ids.includes(originalSelected.value)
                                  ? originalSelected
                                  : null
                      }
                  })
        const selectedProductId = product?.variationValues
            ? product?.variants?.find((v) =>
                  Object.entries(product?.variationValues)?.every(
                      ([k, val]) => v?.variationValues?.[k] === val
                  )
              )?.productId
            : null

        useEffect(() => {
            isFavouriteLoading.current = false // If user logged in, then refresh the current reference.
        }, [customer?.isRegistered, selectedProductId])
        const isFavourite = !!wishlist?.customerProductListItems?.find(
            (item) =>
                (product?.productType?.set ? product?.productId : selectedProductId) ===
                item.productId
        )
        const [selectedVariantId, setSelectedVariantId] = useState(false)
        useEffect(() => {
            const fetchVariant = async () => {
                setChangeSwatchSpinner(true)
                const token = await getTokenWhenReady()
                const requestParams = {id: selectedProductId}

                if (countryISO && currencyCode) {
                    requestParams.c_selectedCountry = countryISO
                    requestParams.c_selectedCurrency = currencyCode
                }
                try {
                    const variantResponse = await api.shopperProducts.getProduct({
                        parameters: requestParams,
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    })
                    let variants = product.variants?.filter(
                        ({productId}) => productId === selectedVariantId
                    )
                    setProductWithFilteredVariants({
                        ...variantResponse,
                        tieredPrices: variants[0].tieredPrices
                    })

                    setSelectedVariantId(null)
                    setChangeSwatchSpinner(false)
                } catch (e) {
                    setChangeSwatchSpinner(false)
                }
                return
            }
            if (selectedVariantId) {
                fetchVariant()
            }
        }, [selectedVariantId])
        return (
            <Flex
                direction={'column'}
                className="product-view-compact"
                data-testid="product-view-compact"
                ref={ref}
                data-is-promo={!!promoId}
            >
                {(showAddToCartSpinner || showChangeSwatchSpinner) && (
                    <LoadingSpinner position="fixed" top="auto" />
                )}
                {/* Basic information etc. title, price, breadcrumb*/}
                {!isProductPartOfSet && (
                    <Box display={['block', 'block', 'block', 'none']}>
                        <ProductViewHeader
                            name={product?.name}
                            product={product}
                            globalePrices={globalePrices}
                            promoId={promoId}
                            priceData={priceData}
                            currency={product?.currency || activeCurrency}
                            isProductPartOfBundle={isProductPartOfBundle}
                            onTileClick={onTileClick}
                            showDescription={showDescription}
                        />
                    </Box>
                )}
                <Flex
                    direction={isProductPartOfSet ? 'row' : ['column', 'column', 'column', 'row']}
                    sx={{
                        '& img': {
                            _focusVisible: {
                                outline: 'none'
                            }
                        }
                    }}
                >
                    {showImageGallery && (
                        <Box width={isProductPartOfSet ? 'auto' : '66.5%'}>
                            {product ? (
                                <ImageGalleryCompact
                                    size={imageSize}
                                    isShopTheLook={isShopTheLook}
                                    product={product}
                                    productIndex={index}
                                    slugUrl={product?.slugUrl}
                                    imageGroups={product.imageGroups}
                                    selectedVariationAttributes={variationParams}
                                    isProductPartOfSet={isProductPartOfSet}
                                    lazy={isProductPartOfSet || isProductPartOfBundle}
                                    onTileClick={onTileClick}
                                    styleVariant={styleVariant}
                                />
                            ) : (
                                <ImageGallerySkeleton />
                            )}
                        </Box>
                    )}

                    {/* Variations & Quantity Selector & CTA buttons */}
                    <VStack
                        data-invalid={showOptionsMessage}
                        className="product-stack"
                        {...styles.viewWrapper.productStack}
                    >
                        <Box
                            display={
                                isProductPartOfSet ? 'block' : ['none', 'none', 'none', 'block']
                            }
                        >
                            <ProductViewHeader
                                name={product?.name}
                                product={product}
                                priceData={priceData}
                                globalePrices={globalePrices}
                                promoId={promoId}
                                currency={product?.currency || activeCurrency}
                                category={category}
                                isShopTheLook={isShopTheLook}
                                isProductPartOfBundle={isProductPartOfBundle}
                                showDescription={showDescription}
                            />
                        </Box>
                        <Box
                            className="variations-stack"
                            {...styles.viewWrapper.variationsStack}
                            {...(styleVariant === 'gwp' ? {marginRight: {lg: '-20px'}} : {})}
                        >
                            {isProductPartOfBundle && (
                                <Box>
                                    <Text fontWeight="medium" fontSize="md" aria-label="price">
                                        <label>
                                            {intl.formatMessage({
                                                defaultMessage: 'Quantity',
                                                id: 'product_view.label.quantity'
                                            })}
                                            : {childOfBundleQuantity}
                                        </label>
                                    </Text>
                                </Box>
                            )}

                            <Stack
                                gap="5"
                                sx={{
                                    'button[aria-checked="false"] div[data-id="letter"]': {
                                        paddingTop: '2px'
                                    }
                                }}
                            >
                                {/*
                                Customize the skeletons shown for attributes to suit your needs. At the point
                                that we show the skeleton we do not know how many variations are selectable. So choose
                                a a skeleton that will meet most of your needs.
                            */}
                                {showLoading ? (
                                    <>
                                        {/* First Attribute Skeleton */}
                                        <Skeleton height={6} width={32} />
                                        <Skeleton height={20} width={64} />

                                        {/* Second Attribute Skeleton */}
                                        <Skeleton height={6} width={32} />
                                        <Skeleton height={20} width={64} />
                                    </>
                                ) : (
                                    showSwatches &&
                                    productVariationAttributes?.map(
                                        ({id, name, selectedValue, values}, index) => {
                                            hasMetal =
                                                (!hasMetal && id == 'metal') ||
                                                (hasMetal && id != 'metal')
                                                    ? true
                                                    : false
                                            const errorStyles = showOptionsMessage
                                                ? {
                                                      borderColor: 'red.400 !important',
                                                      '&:hover': {
                                                          borderColor: 'red.400 !important',
                                                          boxShadow:
                                                              '0px 3px var(--chakra-colors-purple-400) !important'
                                                      }
                                                  }
                                                : {
                                                      '&:hover': {
                                                          borderColor: 'purple.300 !important'
                                                      }
                                                  }
                                            if (id === 'carat') {
                                                return (
                                                    <Select
                                                        key={`${id}-${index}`}
                                                        value={selectedValue?.value || ''}
                                                        {...formStyles.selectField}
                                                        sx={errorStyles}
                                                        invalid={showOptionsMessage.toString()}
                                                        icon={
                                                            <Image
                                                                alt="Arrow"
                                                                src={getUrl(
                                                                    'UIImages/images/down-arrow-new.svg'
                                                                )}
                                                                {...formStyles.selectIcon}
                                                                width="13px"
                                                                height="13px"
                                                                marginRight="15px"
                                                            />
                                                        }
                                                        onChange={(e) => {
                                                            const selectedSize = values.find(
                                                                (size) =>
                                                                    size.value === e.target.value
                                                            )
                                                            const {variants = []} = product
                                                            const selectedVariationValues = {
                                                                ...variant?.variationValues,
                                                                [id]: selectedSize.value
                                                            }
                                                            const selectedVariant =
                                                                variants.filter(
                                                                    ({variationValues}) =>
                                                                        // A single liner that will return true if the current variation has all the
                                                                        // same attribute values as the passing in params.
                                                                        Object.keys(
                                                                            selectedVariationValues
                                                                        ).every(
                                                                            (key) =>
                                                                                variationValues[
                                                                                    key
                                                                                ] ===
                                                                                selectedVariationValues[
                                                                                    key
                                                                                ]
                                                                        )
                                                                )?.[0] || {}
                                                            setSelectedVariantId(
                                                                selectedVariant?.productId
                                                            )
                                                            setProduct({
                                                                ...product,
                                                                ...selectedVariant
                                                            })
                                                        }}
                                                        backgroundColor="white"
                                                    >
                                                        <option value="" disabled>
                                                            {id === 'carat' ? (
                                                                <FormattedMessage
                                                                    defaultMessage="Select a carat size"
                                                                    id="select.a.carat.size"
                                                                />
                                                            ) : (
                                                                <FormattedMessage
                                                                    defaultMessage="Select a size"
                                                                    id="select.a.size"
                                                                />
                                                            )}
                                                        </option>
                                                        {values?.map(({name, value, orderable}) => (
                                                            <option
                                                                key={value}
                                                                value={value}
                                                                disabled={!orderable}
                                                            >
                                                                {name}
                                                            </option>
                                                        ))}
                                                    </Select>
                                                )
                                            }
                                            const swatches = values?.map(
                                                ({href, name, image, value, orderable}, index) => {
                                                    const content = image ? (
                                                        <Box
                                                            height="100%"
                                                            width="100%"
                                                            display={
                                                                index > 3 && hideMoreSwatches
                                                                    ? 'none'
                                                                    : 'block'
                                                            }
                                                            backgroundRepeat="no-repeat"
                                                            backgroundSize="cover"
                                                            fontSize="8px"
                                                            color="black.100"
                                                            data-id={id}
                                                            backgroundColor={
                                                                id === 'letter'
                                                                    ? 'gray.400'
                                                                    : name.toLowerCase()
                                                            }
                                                            style={{
                                                                backgroundImage:
                                                                    id === 'letter'
                                                                        ? 'unset'
                                                                        : `url('${
                                                                              image.disBaseLink ||
                                                                              image.link
                                                                          }')`
                                                            }} // Wrap-up backgroundImage to fix the issues caused by ",".
                                                        >
                                                            {id === 'letter' ? name : ''}
                                                        </Box>
                                                    ) : (
                                                        name
                                                    )
                                                    const hasSelection = Boolean(
                                                        selectedValue?.value
                                                    )
                                                    const isSelected =
                                                        selectedValue?.value === value
                                                    const isFirst = index === 0
                                                    // To mimic the behavior of a native radio input, only
                                                    // one swatch should receive tab focus; the rest can be
                                                    // selected using arrow keys when the swatch group has
                                                    // focus. The focused element is the selected option or
                                                    // the first in the group, if no option is selected.
                                                    // This is a slight difference, for simplicity, from the
                                                    // native element, where the first element is focused on
                                                    // `Tab` and the _last_ element is focused on `Shift+Tab`
                                                    const isFocusable =
                                                        isSelected || (!hasSelection && isFirst)
                                                    const sizeProps = {}
                                                    return (
                                                        <Swatch
                                                            key={`${id}-${value}-${index}`}
                                                            href={href}
                                                            disabled={!orderable}
                                                            value={value}
                                                            name={name}
                                                            ispdp={!isProductPartOfSet}
                                                            isProductPartOfSet={isProductPartOfSet}
                                                            variant={
                                                                id === 'size' || id === 'length'
                                                                    ? 'square'
                                                                    : 'circle'
                                                            }
                                                            selected={isSelected}
                                                            swatchProps={{
                                                                display:
                                                                    index > 2 &&
                                                                    hideMoreSwatches &&
                                                                    id !== 'size'
                                                                        ? 'none'
                                                                        : 'block',
                                                                ...sizeProps
                                                            }}
                                                            isFocusable={isFocusable}
                                                            attributeId={id}
                                                            isCustomSwatch
                                                            customSwatchSelect={(
                                                                variationParams
                                                            ) => {
                                                                const {variants = []} = product
                                                                const selectedVariationValues = {
                                                                    ...variant?.variationValues,
                                                                    ...variationParams
                                                                }
                                                                const selectedVariant =
                                                                    variants?.filter(
                                                                        ({variationValues}) =>
                                                                            // A single liner that will return true if the current variation has all the
                                                                            // same attribute values as the passing in params.
                                                                            Object.keys(
                                                                                selectedVariationValues
                                                                            )?.every(
                                                                                (key) =>
                                                                                    variationValues?.[
                                                                                        key
                                                                                    ] ===
                                                                                    selectedVariationValues[
                                                                                        key
                                                                                    ]
                                                                            )
                                                                    )?.[0] || {}
                                                                setSelectedVariantId(
                                                                    selectedVariant?.productId
                                                                )
                                                                setProduct({
                                                                    ...product,
                                                                    ...selectedVariant
                                                                })
                                                            }}
                                                        >
                                                            {content}
                                                        </Swatch>
                                                    )
                                                }
                                            )
                                            return (
                                                <Box key={id}>
                                                    {id === 'size' ||
                                                    (isShopTheLook && id === 'metal') ? (
                                                        <Box
                                                            fontWeight="400"
                                                            fontSize="16px"
                                                            color="black.100"
                                                            display="inline-flex"
                                                        >
                                                            {id === 'size' ? 'Size' : name}
                                                            <Box fontWeight="300" marginLeft="15px">
                                                                {selectedValue?.name ||
                                                                    `Select a ${
                                                                        id === 'size' ? id : name
                                                                    }`}
                                                            </Box>
                                                        </Box>
                                                    ) : null}

                                                    <SwatchGroup
                                                        groupProps={{}}
                                                        value={selectedValue?.value}
                                                        displayName={selectedValue?.name || ''}
                                                        onClick={(e) => {
                                                            e.preventDefault()
                                                        }}
                                                        handleChange={(e) => {
                                                            if (typeof e !== 'string') {
                                                                setHideMoreSwatches(false)
                                                            }
                                                        }}
                                                        pdpSwatches={product?.c_PDPSwatches}
                                                        label={
                                                            isProductPartOfSet
                                                                ? isShopTheLook
                                                                    ? intl.formatMessage(
                                                                          {
                                                                              defaultMessage:
                                                                                  'Select a {groupname}',
                                                                              id: 'product_view.label.group_variant_type'
                                                                          },
                                                                          {groupname: name}
                                                                      )
                                                                    : ''
                                                                : intl.formatMessage(
                                                                      {
                                                                          defaultMessage:
                                                                              '{variantType}',
                                                                          id: 'product_view.label.variant_type'
                                                                      },
                                                                      {variantType: name}
                                                                  )
                                                        }
                                                        showLabel={
                                                            hasMetal &&
                                                            id !== 'size' &&
                                                            !(isShopTheLook && id === 'metal')
                                                        }
                                                        isShopTheLook={isShopTheLook}
                                                    >
                                                        {swatches}
                                                        {values?.length > 3 &&
                                                            hideMoreSwatches &&
                                                            id !== 'size' && (
                                                                <LinkPlaceholder
                                                                    onClick={(e) => {
                                                                        e.preventDefault()
                                                                        setHideMoreSwatches(false)
                                                                    }}
                                                                    alignContent="center"
                                                                    _hover={{
                                                                        textDecoration: 'none'
                                                                    }}
                                                                    textDecoration="none"
                                                                >
                                                                    <Text
                                                                        color="black.100"
                                                                        fontWeight="300"
                                                                        fontSize="13px"
                                                                    >
                                                                        <FormattedMessage
                                                                            defaultMessage="+ {count} more"
                                                                            values={{
                                                                                count:
                                                                                    values.length -
                                                                                    3
                                                                            }}
                                                                            id="product_tile.button.more_swatches"
                                                                        />
                                                                    </Text>
                                                                </LinkPlaceholder>
                                                            )}
                                                    </SwatchGroup>
                                                </Box>
                                            )
                                        }
                                    )
                                )}
                            </Stack>

                            <Box ref={errorContainerRef}>
                                {!showLoading && showOptionsMessage && (
                                    <Fade in={true}>
                                        <Text {...styles.viewWrapper.invalidSelection} mt={2}>
                                            {intl.formatMessage({
                                                defaultMessage:
                                                    'Please select all your options above'
                                            })}
                                        </Text>
                                    </Fade>
                                )}
                            </Box>
                        </Box>

                        <Box marginLeft="20px" className="product-view__actions">
                            {!showLoading &&
                                showInventoryMessage &&
                                !customInventoryMessage &&
                                !isProductPartOfSet && (
                                    <Fade in={true}>
                                        <Text color="orange.600" fontWeight={600} marginBottom={8}>
                                            {inventoryMessage}
                                        </Text>
                                    </Fade>
                                )}
                            {!showLoading && customInventoryMessage && !isProductPartOfSet && (
                                <Fade in={true}>
                                    <Text color="orange.600" fontWeight={600} marginBottom={8}>
                                        {customInventoryMessage}
                                    </Text>
                                </Fade>
                            )}
                            <Box {...styles.actionWrapper}>
                                {showQtySelector && (
                                    <QuantityPicker
                                        id="quantity"
                                        step={1}
                                        value={quantity}
                                        min={1}
                                        max={maxOrderQuantity}
                                        isDisabled={!enableQtySelector}
                                        onChange={(stringValue, numberValue) => {
                                            // Set the Quantity of product to value of input if value number
                                            if (numberValue >= 0) {
                                                setQuantity(numberValue)
                                            } else if (stringValue === '') {
                                                // We want to allow the use to clear the input to start a new input so here we set the quantity to '' so NAN is not displayed
                                                // User will not be able to add '' quantity to the cart due to the add to cart button enablement rules
                                                setQuantity(stringValue)
                                            }
                                        }}
                                        onBlur={(e) => {
                                            // Default to 1the `minOrderQuantity` if a user leaves the box with an invalid value
                                            const value = e.target.value
                                            if (parseInt(value) < 0 || value === '') {
                                                setQuantity(1)
                                            }
                                        }}
                                        onFocus={(e) => {
                                            // This is useful for mobile devices, this allows the user to pop open the keyboard and set the
                                            // new quantity with one click. NOTE: This is something that can be refactored into the parent
                                            // component, potentially as a prop called `selectInputOnFocus`.
                                            e.target.select()
                                        }}
                                        productName={product?.name}
                                    />
                                )}

                                {showAddToBag && renderActionButtons()}

                                {enableFavourite && countryISO === 'US' && (
                                    <Box
                                        className="wishlist-wrapper"
                                        width="fit-content"
                                        position="relative"
                                        onClick={(e) => {
                                            // stop click event from bubbling
                                            // to avoid user from clicking the underlying
                                            // product while the favourite icon is disabled
                                            e.preventDefault()
                                        }}
                                    >
                                        {/* Animated Hearts */}
                                        {wishlistAnimation === index && (
                                            <Box
                                                position="absolute"
                                                right="30px"
                                                top="9px"
                                                zIndex={1}
                                            >
                                                <MotionBox
                                                    top="-10px"
                                                    left="7px"
                                                    animate={{
                                                        opacity: [1, 0],
                                                        x: [0, 5],
                                                        y: [0, -10],
                                                        rotate: [0, 25]
                                                    }}
                                                    {...wishlistStyles.motionBox}
                                                >
                                                    <Image
                                                        src={getUrl(
                                                            'UIImages/images/fill-heart-icon.svg'
                                                        )}
                                                        alt="heart right"
                                                        width="100%"
                                                        height="100%"
                                                    />
                                                </MotionBox>

                                                <MotionBox
                                                    top="-11px"
                                                    left="7px"
                                                    animate={{
                                                        opacity: [1, 0],
                                                        x: [0, -7],
                                                        y: [0, -11],
                                                        rotate: [0, -25]
                                                    }}
                                                    {...wishlistStyles.motionBox}
                                                    transition={{duration: 0.7, ease: 'easeInOut'}}
                                                >
                                                    <Image
                                                        src={getUrl(
                                                            'UIImages/images/fill-heart-icon.svg'
                                                        )}
                                                        alt="heart left"
                                                        width="100%"
                                                        height="100%"
                                                    />
                                                </MotionBox>

                                                <MotionBox
                                                    top="-20px"
                                                    left="7px"
                                                    animate={{
                                                        opacity: [1, 0],
                                                        x: [0, 4],
                                                        y: [0, -20],
                                                        rotate: [0, 20]
                                                    }}
                                                    {...wishlistStyles.motionBox}
                                                >
                                                    <Image
                                                        src={getUrl(
                                                            'UIImages/images/fill-heart-icon.svg'
                                                        )}
                                                        alt="heart top"
                                                        width="100%"
                                                        height="100%"
                                                    />
                                                </MotionBox>
                                            </Box>
                                        )}
                                        <IconButtonWithRegistration
                                            data-testid="wishlist-button"
                                            backgroundColor="transparent"
                                            _hover={{
                                                backgroundColor: 'gray.100'
                                            }}
                                            _focusVisible={{
                                                boxShadow: 'none'
                                            }}
                                            borderRadius="50%"
                                            aria-label={
                                                isFavourite
                                                    ? intl.formatMessage(
                                                          {
                                                              id: 'product_tile.assistive_msg.remove_from_wishlist',
                                                              defaultMessage:
                                                                  'Remove {product} from wishlist'
                                                          },
                                                          {product: variant?.productName}
                                                      )
                                                    : intl.formatMessage(
                                                          {
                                                              id: 'product_tile.assistive_msg.add_to_wishlist',
                                                              defaultMessage:
                                                                  'Add {product} to wishlist'
                                                          },
                                                          {product: variant?.productName}
                                                      )
                                            }
                                            icon={
                                                isFavourite ? (
                                                    <Image
                                                        width="15px"
                                                        alt="filled heart"
                                                        height="15px"
                                                        src={getUrl(
                                                            'UIImages/images/fill-heart-icon.svg'
                                                        )}
                                                    />
                                                ) : (
                                                    <Image
                                                        src={getUrl(
                                                            'UIImages/images/empty-heart-icon.svg'
                                                        )}
                                                        width="15px"
                                                        alt="empty heart"
                                                        height="15px"
                                                    />
                                                )
                                            }
                                            onClick={async () => {
                                                if (!variant?.productId) {
                                                    toggleShowOptionsMessage(true)
                                                    return null
                                                }
                                                if (showOptionsMessage) {
                                                    toggleShowOptionsMessage(false)
                                                }
                                                if (!isFavouriteLoading.current) {
                                                    isFavouriteLoading.current = true
                                                    await onFavouriteToggle(
                                                        !isFavourite,
                                                        variant?.productId
                                                    )
                                                    isFavouriteLoading.current = false
                                                }
                                            }}
                                            minWidth="31px"
                                            height="31px"
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </VStack>
                </Flex>
            </Flex>
        )
    }
)

ProductViewCompact.displayName = 'ProductViewCompact'

ProductViewCompact.propTypes = {
    product: PropTypes.object,
    globalePrices: PropTypes.any,
    promoId: PropTypes.any,
    isProductPartOfSet: PropTypes.bool,
    isProductPartOfBundle: PropTypes.bool,
    childOfBundleQuantity: PropTypes.number,
    ispdp: PropTypes.bool,
    meterType: PropTypes.any,
    category: PropTypes.array,
    isProductLoading: PropTypes.bool,
    isBasketLoading: PropTypes.bool,
    isWishlistLoading: PropTypes.bool,
    index: PropTypes.any,
    onFavouriteToggle: PropTypes.func,
    enableFavourite: PropTypes.bool,
    wishlistAnimation: PropTypes.bool,
    addToCart: PropTypes.func,
    addToWishlist: PropTypes.func,
    updateCart: PropTypes.func,
    updateWishlist: PropTypes.func,
    imageSize: PropTypes.oneOf(['sm', 'md']),
    childProductOrderability: PropTypes.object,
    onVariantSelected: PropTypes.func,
    validateOrderability: PropTypes.func,
    showImageGallery: PropTypes.bool,
    setSelectedBundleQuantity: PropTypes.func,
    isShopTheLook: PropTypes.bool,
    wishlist: PropTypes.object,
    selectedBundleParentQuantity: PropTypes.number,
    onTileClick: PropTypes.func,
    showQtySelector: PropTypes.bool,
    enableQtySelector: PropTypes.bool,
    showAddToBag: PropTypes.bool,
    maxOrderQuantity: PropTypes.number,
    styleVariant: PropTypes.string,
    enableAddToBag: PropTypes.bool,
    showSwatches: PropTypes.bool,
    showDescription: PropTypes.bool,
    limitReached: PropTypes.bool,
    showAddedToBagLabel: PropTypes.bool
}

export default ProductViewCompact
